import React, { useState } from 'react';
import { css } from '@emotion/core';
import { theme } from '@weave/theme-original';
import {
  LockIcon,
  NakedButton,
  PrimaryButton,
  SecondaryButton,
  Text,
  useTooltip,
} from '@weave/design-system';
import {
  type UnifyWizardStep,
  useUnifyHandoffContext,
} from '../../unify-handoff-context';
import { SaveExceptionsConfirmModal } from './save-exceptions-confirm-modal';

export interface UnifyHandoffFooterProps {
  step?: UnifyWizardStep;
  canProceed?: boolean;
  canSubmit?: boolean;
  hasMinSelections?: boolean;
  hasExceptions?: boolean;
  onNext?: () => void;
  onSubmit?: () => void;
  onCancel?: () => void;
  onSaveAndClose?: () => void;
  isSaving?: boolean;
  isSubmitting?: boolean;
}

export const UnifyHandoffFooter: React.FC<UnifyHandoffFooterProps> = ({
  step,
  canProceed,
  canSubmit = false,
  hasMinSelections,
  hasExceptions = false,
  onNext,
  onSubmit,
  onCancel,
  onSaveAndClose,
  isSaving,
  isSubmitting,
}) => {
  const context = useUnifyHandoffContext();
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  const { triggerProps, tooltipProps, Tooltip } = useTooltip({ placement: 'top' });

  const effectiveStep = step ?? context.currentStep;
  const effectiveIsSaving = isSaving !== undefined ? isSaving : context.isSaving;
  const effectiveIsSubmitting =
    isSubmitting !== undefined ? isSubmitting : context.isSubmitting;
  const effectiveCanProceed =
    canProceed !== undefined ? canProceed : context.canProceedToEvaluation;
  const effectiveHasMinSelections =
    hasMinSelections !== undefined ? hasMinSelections : context.hasMinSelections;

  const handleCancel = onCancel ?? context.onCancel;
  const handleSaveAndClose = onSaveAndClose ?? context.onSaveAndClose;
  const handleNext = onNext ?? context.goToNextStep;

  const handleSaveAndCloseClick = () => {
    if (hasExceptions) {
      setIsConfirmModalOpen(true);
    } else {
      handleSaveAndClose();
    }
  };

  const handleConfirmSave = () => {
    setIsConfirmModalOpen(false);
    handleSaveAndClose();
  };

  const handleCloseConfirmModal = () => {
    setIsConfirmModalOpen(false);
  };

  // Snapshot mode has no footer actions
  if (effectiveStep === 'snapshot') {
    return null;
  }

  const isActionDisabled =
    effectiveStep === 'select-locations'
      ? effectiveIsSaving
      : effectiveIsSaving || effectiveIsSubmitting;
  const isSaveDisabled =
    isActionDisabled ||
    (effectiveStep === 'select-locations' && !effectiveHasMinSelections);
  const isSubmitDisabled = !canSubmit || isActionDisabled;

  return (
    <div css={styles.footer}>
      <div css={styles.actionItem}>
        <NakedButton onClick={handleCancel} disabled={isActionDisabled}>
          <Text as="span" color="primary">
            Cancel
          </Text>
        </NakedButton>
      </div>

      <div css={styles.actionItem}>
        <SecondaryButton onClick={handleSaveAndCloseClick} disabled={isSaveDisabled}>
          {effectiveIsSaving ? 'Saving...' : 'Save & Close'}
        </SecondaryButton>
      </div>

      <div css={styles.actionItem}>
        {effectiveStep === 'select-locations' ? (
          <PrimaryButton
            onClick={handleNext}
            disabled={!effectiveCanProceed || effectiveIsSaving}
          >
            Next
          </PrimaryButton>
        ) : (
          <PrimaryButton
            onClick={onSubmit}
            disabled={isSubmitDisabled}
            title={
              effectiveIsSubmitting ? 'Submitting...' : 'Submit for Selected Locations'
            }
            {...(isSubmitDisabled && !effectiveIsSubmitting ? triggerProps : {})}
          >
            <span css={styles.submitLabel}>
              {isSubmitDisabled && !effectiveIsSubmitting && (
                <LockIcon color="light" size={14} />
              )}
              {effectiveIsSubmitting ? 'Submitting...' : 'Submit for Selected Locations'}
            </span>

            {!effectiveIsSubmitting && (
              <Tooltip {...tooltipProps} css={styles.tooltip}>
                Add exceptions and resolve the required checks to continue
              </Tooltip>
            )}
          </PrimaryButton>
        )}
      </div>

      <SaveExceptionsConfirmModal
        isOpen={isConfirmModalOpen}
        onConfirm={handleConfirmSave}
        onClose={handleCloseConfirmModal}
        isSaving={effectiveIsSaving}
      />
    </div>
  );
};

const styles = {
  footer: css`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: ${theme.spacing(2)};
    margin-top: ${theme.spacing(3)};
    padding-bottom: ${theme.spacing(2)};
  `,
  actionItem: css`
    display: inline-flex;
  `,
  submitLabel: css`
    display: inline-flex;
    align-items: center;
    gap: ${theme.spacing(0.75)};
  `,
  tooltip: css`
    && {
      max-width: 280px;
    }
  `,
};
